import { TFunction } from 'i18next';
import parsePhoneNumber from 'libphonenumber-js';
import { getLangNameFromCode as baseGetLangNameFromCode } from 'language-name-map';

import { Routes } from 'config';
import { Nullable, CourseStep, JourneyStage } from 'types';

export const joinStrings = (
  values: Nullable<string>[],
  separator: string,
  placeholder: string | null = '-'
): string => {
  const data = values.filter((value) => value && value?.length > 0) as string[];
  return data.length === 0 ? (placeholder ?? '') : data.join(separator);
};

export const michiganCounties = [
  'Alcona',
  'Alger',
  'Allegan',
  'Alpena',
  'Antrim',
  'Arenac',
  'Baraga',
  'Barry',
  'Bay',
  'Benzie',
  'Berrien',
  'Branch',
  'Calhoun',
  'Cass',
  'Charlevoix',
  'Cheboygan',
  'Chippewa',
  'Clare',
  'Clinton',
  'Crawford',
  'Delta',
  'Dickinson',
  'Eaton',
  'Emmet',
  'Genesee',
  'Gladwin',
  'Gogebic',
  'Grand Traverse',
  'Gratiot',
  'Hillsdale',
  'Houghton',
  'Huron',
  'Ingham',
  'Ionia',
  'Iosco',
  'Iron',
  'Isabella',
  'Jackson',
  'Kalamazoo',
  'Kalkaska',
  'Kent',
  'Keweenaw',
  'Lake',
  'Lapeer',
  'Leelanau',
  'Lenawee',
  'Livingston',
  'Luce',
  'Mackinac',
  'Macomb',
  'Manistee',
  'Marquette',
  'Mason',
  'Mecosta',
  'Menominee',
  'Midland',
  'Missaukee',
  'Monroe',
  'Montcalm',
  'Montmorency',
  'Muskegon',
  'Newaygo',
  'Oakland',
  'Oceana',
  'Ogemaw',
  'Ontonagon',
  'Osceola',
  'Oscoda',
  'Otsego',
  'Ottawa',
  'Presque Isle',
  'Roscommon',
  'Saginaw',
  'St. Clair',
  'St. Joseph',
  'Sanilac',
  'Schoolcraft',
  'Shiawassee',
  'Tuscola',
  'Van Buren',
  'Washtenaw',
  'Wayne',
  'Wexford',
];

export const countyOptions = michiganCounties.map((value) => ({
  value,
  label: value,
}));

export const stringToBoolean = (value: string): boolean => value === 'true';

export const booleanToString = (value: boolean): string =>
  value ? 'true' : 'false';

export const codeNameToJourneyStep = (codeName: string) => {
  const enumValues = Object.values(JourneyStage) as string[];
  const mainPartOfCode = codeName.split('_')[0];
  const type = enumValues.includes(mainPartOfCode)
    ? (mainPartOfCode as JourneyStage)
    : codeName;
  return type;
};

export const getNavigationPath = (
  currentStepCodeName: string,
  nextStep: CourseStep
) => {
  const { id, resourceVersions } = nextStep;
  const stepType = codeNameToJourneyStep(currentStepCodeName);
  if (stepType === JourneyStage.VACCINE) {
    const nextStepType = codeNameToJourneyStep(nextStep.codeName);
    if (stepType === nextStepType) {
      const currentStepIndex = currentStepCodeName.split('_')[1];
      const nextStepIndex = nextStep.codeName.split('_')[1];
      if (currentStepIndex !== nextStepIndex) {
        return Routes.Course.Vaccine;
      }
    }
  }
  const isResource = resourceVersions && resourceVersions.length > 0;
  if (isResource) {
    return Routes.Course.Resource.replace(':id', id);
  } else {
    return Routes.Course.Form.replace(':id', id);
  }
};

export const isPromise = (value: any) => {
  return (
    value != null &&
    typeof value === 'object' &&
    typeof value.then === 'function'
  );
};

export const formatPhone = (
  phone?: string,
  placeholder: string = '-',
  national: boolean = true
) => {
  if (!phone) {
    return placeholder;
  }
  const phoneNumber = parsePhoneNumber(phone);
  if (!phoneNumber) {
    return phone;
  }
  return national
    ? phoneNumber.formatNational()
    : phoneNumber.formatInternational();
};

type Language = { name: string; native: string; dir: 'ltr' | 'rtl' };

const fallbackLangs: Record<string, Language> = {
  hmn: { dir: 'ltr', name: 'Hmong', native: 'Hmoob' },
};

export const getLangNameFromCode = (langCode: string): Language | undefined => {
  const lang = baseGetLangNameFromCode(langCode);

  if (lang) {
    return lang;
  }

  return fallbackLangs[langCode];
};

export const getStepTitle = (
  t: TFunction,
  type: string,
  step: CourseStep,
  language?: string
) => {
  const translated = t(type);
  if (type !== translated) {
    return translated;
  }
  return (language && step?.translations?.[language]?.name) || step?.name;
};

export const clearCacheAndReload = async () => {
  const keyList = await caches.keys();
  await Promise.all(keyList.map((key) => caches.delete(key)));

  window.location.reload();
};
